'use client';

import React from 'react';
import { Mic, ChevronUp, CheckCircle, X, Radio } from 'lucide-react';
import type { GossipRow, GossipStatus } from './GossipDetailDrawer';

interface LiveQueueProps {
  items: GossipRow[];
  onStatusChange: (id: string, status: GossipStatus) => void;
  onRemoveFromQueue: (id: string) => void;
  onOpenDetail: (gossip: GossipRow) => void;
}

const categoryColors: Record<string, string> = {
  Amor: 'bg-pink-100 text-pink-700',
  Trabajo: 'bg-blue-100 text-blue-700',
  Familia: 'bg-orange-100 text-orange-700',
  Vecinos: 'bg-green-100 text-green-700',
  Famosos: 'bg-purple-100 text-purple-700',
  Otro: 'bg-gray-100 text-gray-700',
};

export default function LiveQueue({ items, onStatusChange, onRemoveFromQueue, onOpenDetail }: LiveQueueProps) {
  const queueItems = items.filter((i) => i.status === 'en-cola' || i.status === 'contando');
  const contando = queueItems.find((i) => i.status === 'contando');
  const enCola = queueItems.filter((i) => i.status === 'en-cola');

  return (
    <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-gradient-to-r from-primary/5 to-secondary/5">
        <div className="flex items-center gap-2">
          <Radio size={16} className="text-primary" />
          <h3 className="font-bold text-foreground text-base">Cola de Transmisión en Vivo</h3>
        </div>
        <div className="flex items-center gap-2">
          {contando && (
            <span className="flex items-center gap-1.5 bg-red-100 text-red-700 border border-red-200 text-xs font-bold px-3 py-1.5 rounded-full live-pulse">
              <span className="w-1.5 h-1.5 bg-red-500 rounded-full dot-pulse" />
              Contando ahora
            </span>
          )}
          <span className="bg-secondary/10 text-secondary text-xs font-bold px-2.5 py-1 rounded-full">
            {enCola.length} en cola
          </span>
        </div>
      </div>

      <div className="p-4 flex flex-col gap-3">
        {queueItems.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-3xl mb-2">📭</p>
            <p className="font-semibold text-foreground text-sm mb-1">Cola vacía</p>
            <p className="text-xs text-muted-foreground">Agrega chismes desde la tabla de abajo</p>
          </div>
        ) : (
          <>
            {/* Currently telling */}
            {contando && (
              <div className="bg-primary/8 border-2 border-primary/30 rounded-xl p-4 relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary via-secondary to-primary animate-pulse" />
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-primary/15 rounded-xl flex items-center justify-center shrink-0 mt-0.5">
                    <Mic size={14} className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-xs font-bold text-primary uppercase tracking-wide">🎙️ Contando ahora</span>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${categoryColors[contando.category] || categoryColors['Otro']}`}>
                        {contando.category}
                      </span>
                    </div>
                    <p
                      className="text-sm text-foreground leading-snug line-clamp-2 cursor-pointer hover:text-primary transition-colors"
                      onClick={() => onOpenDetail(contando)}
                    >
                      {contando.text}
                    </p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <ChevronUp size={11} className="text-primary" />
                        {contando.votes} votos
                      </span>
                      <span className="text-xs text-muted-foreground">👤 {contando.nickname}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => onStatusChange(contando.id, 'contado')}
                    className="shrink-0 flex items-center gap-1.5 bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 rounded-xl px-3 py-2 text-xs font-semibold transition-all"
                  >
                    <CheckCircle size={13} />
                    Listo
                  </button>
                </div>
              </div>
            )}

            {/* Queue items */}
            {enCola.length > 0 && (
              <div className="flex flex-col gap-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-1">
                  Próximos en cola ({enCola.length})
                </p>
                {enCola.map((item, idx) => (
                  <div
                    key={item.id}
                    className="flex items-start gap-3 bg-muted/40 border border-border hover:border-primary/20 rounded-xl p-3.5 group transition-all"
                  >
                    <div className="w-6 h-6 bg-secondary/10 text-secondary rounded-lg flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p
                        className="text-sm text-foreground leading-snug line-clamp-2 cursor-pointer hover:text-primary transition-colors"
                        onClick={() => onOpenDetail(item)}
                      >
                        {item.text}
                      </p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${categoryColors[item.category] || categoryColors['Otro']}`}>
                          {item.category}
                        </span>
                        <span className="text-xs text-muted-foreground flex items-center gap-0.5">
                          <ChevronUp size={10} className="text-primary" />
                          {item.votes}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => onStatusChange(item.id, 'contando')}
                        title="Contar ahora"
                        className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all"
                        aria-label="Contar ahora"
                      >
                        <Mic size={13} />
                      </button>
                      <button
                        onClick={() => onRemoveFromQueue(item.id)}
                        title="Quitar de la cola"
                        className="p-1.5 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-50 transition-all"
                        aria-label="Quitar de cola"
                      >
                        <X size={13} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
